import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import TeacherNotificationBell from "./TeacherNotificationBell";
import BookmarkIcon from "./BookmarkIcon";
import StreakBadge from "./StreakBadge";
import LanguageSwitcher from "./LanguageSwitcher";
import { useLanguage } from "../i18n/LanguageContext";
import { useIsSchoolStaff } from "./school/SchoolHub";
import { schoolApi } from "../lib/school";
import {
  Home, BookOpen, BookMarked, Users, GraduationCap, LogOut, Inbox,
  BarChart3, ShieldCheck, Play, UserRound, CalendarClock, Building2,
  Menu, X, Sparkles, Languages, ScrollText,
} from "lucide-react";

const studentLinks = [
  { to: "/app", icon: Home, labelKey: "nav.home", testid: "nav-home" },
  { to: "/app/quran", icon: BookOpen, labelKey: "nav.myQuran", testid: "nav-quran" },
  { to: "/app/tadabbur", icon: Sparkles, labelKey: "nav.tadabbur", testid: "nav-tadabbur" },
  { to: "/app/hifz", icon: BookMarked, labelKey: "nav.myHifz", testid: "nav-myhifz" },
  { to: "/app/basic-hifz", icon: ScrollText, labelKey: "nav.basicHifz", testid: "nav-basic-hifz" },
  { to: "/app/community", icon: Users, labelKey: "nav.community", testid: "nav-community" },
  { to: "/app/teachers", icon: GraduationCap, labelKey: "nav.teachers", testid: "nav-teachers" },
  // C8 — Voice Notes removed as its own sidebar entry; it's reachable
  // from the Teachers page instead now (see Teachers.jsx).
  { to: "/app/halaqa", icon: CalendarClock, labelKey: "nav.myHalaqa", testid: "nav-halaqa" },
  { to: "/app/profile", icon: UserRound, labelKey: "nav.profile", testid: "nav-profile" },
];

const teacherLinks = [
  { to: "/app/teacher/grading", icon: Inbox, labelKey: "nav.gradingInbox", testid: "nav-grading" },
  { to: "/app/teacher/halaqa-pulse", icon: BarChart3, labelKey: "nav.halaqaPulse", testid: "nav-halaqa-pulse" },
  { to: "/app/teacher", icon: GraduationCap, labelKey: "nav.teacherHub", testid: "nav-teacher" },
  { to: "/app/bookings", icon: CalendarClock, labelKey: "nav.bookings", testid: "nav-bookings" },
  { to: "/app/teacher-apply", icon: ShieldCheck, labelKey: "nav.getVerified", testid: "nav-teacher-apply" },
];

const adminLinks = [
  { to: "/app/admin", icon: ShieldCheck, labelKey: "nav.adminPanel", testid: "nav-admin" },
  { to: "/app/admin/teacher-apps", icon: GraduationCap, labelKey: "nav.teacherApplications", testid: "nav-teacher-apps" },
];

// A translator only works on the languages assigned to them; the admin panel shows them just that tab.
const translatorLinks = [
  { to: "/app/admin", icon: Languages, labelKey: "nav.translations", testid: "nav-translations" },
];

const orgAdminLinks = [
  { to: "/app/org-admin", icon: Building2, labelKey: "nav.orgAdmin", testid: "nav-org-admin" },
];

/* The full nav list, shown inside the hamburger drawer (see MobileTopbar
   below) — the app always lives inside a fixed-width "phone frame" now (see
   AppLayout.jsx), so there's no separate desktop sidebar surface anymore;
   this is the only nav list left. `onNavigate` closes the drawer on tap. */
function NavGroups({ user, onNavigate }) {
  const { t } = useLanguage();
  const isTeacherish = user?.role === "mentor" || user?.role === "admin";
  // School principals and teachers also get a "School" page (a read-only view of their school, shared by every app).
  const { staff: isSchoolStaff, role: schoolRole } = useIsSchoolStaff(schoolApi, !!user);

  return (
    <>
      {studentLinks.map((l) => {
        // The special-case for "/app/quran?tab=search" (C5) was dropped
        // along with the standalone "Search" nav item — with only "My Quran"
        // left on that path, plain isActive is correct again.
        return (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === "/app"}
            onClick={onNavigate}
            data-testid={l.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-forest-600 text-sand-100 shadow-sm"
                  : "text-foreground/80 hover:bg-sand-200"
              }`
            }
          >
            <l.icon className="h-4 w-4" />
            <span>{t(l.labelKey)}</span>
          </NavLink>
        );
      })}

      {isTeacherish && (
        <>
          <div className="px-4 pt-5 pb-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{t("nav.section.teachers")}</div>
          {teacherLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={onNavigate}
              data-testid={l.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive ? "bg-gold-500 text-foreground" : "text-foreground/80 hover:bg-sand-200"
                }`
              }
            >
              <l.icon className="h-4 w-4" />
              <span>{t(l.labelKey)}</span>
            </NavLink>
          ))}
        </>
      )}

      {user?.role === "admin" && (
        <>
          <div className="px-4 pt-5 pb-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{t("nav.section.administration")}</div>
          {adminLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={onNavigate}
              data-testid={l.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive ? "bg-terracotta-500 text-white" : "text-foreground/80 hover:bg-sand-200"
                }`
              }
            >
              <l.icon className="h-4 w-4" />
              <span>{t(l.labelKey)}</span>
            </NavLink>
          ))}
        </>
      )}

      {isSchoolStaff && (
        <NavLink
          to="/app/school"
          onClick={onNavigate}
          data-testid="nav-school"
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
              isActive ? "bg-terracotta-500 text-white" : "text-foreground/80 hover:bg-sand-200"
            }`
          }
        >
          <GraduationCap className="h-4 w-4" />
          <span>{schoolRole === "principal" ? t("nav.school_principal") : t("nav.school_teacher")}</span>
        </NavLink>
      )}

      {user?.role === "translator" && (
        <>
          <div className="px-4 pt-5 pb-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{t("nav.section.administration")}</div>
          {translatorLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={onNavigate}
              data-testid={l.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive ? "bg-terracotta-500 text-white" : "text-foreground/80 hover:bg-sand-200"
                }`
              }
            >
              <l.icon className="h-4 w-4" />
              <span>{t(l.labelKey)}</span>
            </NavLink>
          ))}
        </>
      )}

      {user?.role === "org_admin" && (
        <>
          <div className="px-4 pt-5 pb-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{t("nav.section.orgAdministration")}</div>
          {orgAdminLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={onNavigate}
              data-testid={l.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive ? "bg-terracotta-500 text-white" : "text-foreground/80 hover:bg-sand-200"
                }`
              }
            >
              <l.icon className="h-4 w-4" />
              <span>{t(l.labelKey)}</span>
            </NavLink>
          ))}
        </>
      )}
    </>
  );
}

/* User card (avatar, name, role, streak/ayahs/xp, sign out), pinned at the
   bottom of the hamburger drawer. */
function UserCard({ user, onLogout }) {
  const { t } = useLanguage();
  const roleKey = { student: "user.role.student", mentor: "user.role.mentor", teacher: "user.role.teacher", admin: "user.role.admin", principal: "user.role.principal", translator: "user.role.translator", org_admin: "user.role.orgAdmin" }[user?.role || "student"];
  return (
    <div className="p-4 m-4 rounded-2xl bg-sand-200/80 border border-sand-300">
      <div className="flex items-center gap-3">
        {user?.picture ? (
          <img src={user.picture} alt="" className="h-10 w-10 rounded-full object-cover" />
        ) : (
          <div className="h-10 w-10 rounded-full bg-forest-700 text-sand-100 grid place-items-center font-display font-semibold">
            {(user?.name || "U")[0].toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">{user?.name || t("user.defaultName")}</div>
          <div className="text-xs text-muted-foreground capitalize flex items-center gap-1.5">
            <span>{roleKey ? t(roleKey) : user?.role}</span>
            {user?.role === "mentor" && user?.teacher_status !== "approved" && (
              <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-terracotta-500/15 text-terracotta-600 font-semibold" data-testid="pending-badge">{t("user.pending")}</span>
            )}
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={onLogout}
          className="p-2 rounded-lg hover:bg-sand-300 transition"
          title={t("user.signOut")}
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <div><div className="text-lg font-display font-semibold text-forest-700">{user?.streak ?? 0}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("user.streak")}</div></div>
        <div><div className="text-lg font-display font-semibold text-forest-700">{user?.total_memorized ?? 0}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("user.ayahs")}</div></div>
        <div><div className="text-lg font-display font-semibold text-gold-600">{user?.xp ?? 0}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("user.xp")}</div></div>
      </div>
    </div>
  );
}

/* The old desktop-only <aside> sidebar (shown lg:flex, hidden below 1024px)
   was retired here — the app now always renders inside a fixed-width
   "phone frame" (AppLayout.jsx), on a laptop or a phone alike, so there's
   no more "desktop layout" branch left for it to serve. MobileTopbar below
   (hamburger + drawer, using NavGroups/UserCard above) is now the only nav
   chrome, shown unconditionally. */

/* Desktop sidebar: shown from lg (1024px) up, where the app runs full width
   instead of inside the phone frame. Same nav list as the mobile drawer. */
export function DesktopSidebar() {
  const { user, logout } = useAuth();
  return (
    <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-sand-300 bg-sand-100 h-full" data-testid="desktop-sidebar">
      <div className="px-6 pb-4 pt-6">
        <Link to="/app" className="flex items-center gap-2.5">
          <img src="/Hikma-Verse-Logo.png" alt="Hikmah Verses" className="h-9 w-9 rounded-xl object-cover" />
          <div>
            <div className="font-display font-semibold text-forest-700 leading-tight">Hifz with Hikmah</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">HikmahVerses</div>
          </div>
        </Link>
      </div>
      <div className="ornamental-line mx-7 mb-3" />
      <nav className="px-4 flex-1 min-h-0 overflow-y-auto space-y-1">
        <NavGroups user={user} />
      </nav>
      <LanguageSwitcher className="mx-4 mt-3" />
      <UserCard user={user} onLogout={async () => { await logout(); }} />
    </aside>
  );
}

export function MobileTopbar() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="lg:hidden shrink-0 z-30 flex items-center justify-between border-b border-sand-300 bg-white/70 backdrop-blur px-5 pb-3 pt-[calc(0.75rem_+_env(safe-area-inset-top))]">
        <button
          onClick={() => setOpen(true)}
          className="p-2 -ml-2 rounded-lg hover:bg-sand-200 transition"
          aria-label={t("nav.openMenu")}
          data-testid="mobile-menu-btn"
        >
          <Menu className="h-5 w-5 text-forest-700" />
        </button>
        <Link to="/app" className="flex min-w-0 items-center gap-2">
          <img src="/Hikma-Verse-Logo.png" alt="Hikmah Verses" className="h-8 w-8 shrink-0 rounded-lg object-cover" />
          <span className="font-display font-semibold text-forest-700 text-sm truncate max-[380px]:hidden">Hifz with Hikmah</span>
        </Link>
        <div className="flex items-center gap-1">
          <LanguageSwitcher variant="icon" />
          <BookmarkIcon />
          <StreakBadge />
          <TeacherNotificationBell />
        </div>
      </div>

      {open && (
        // absolute (not fixed) so this anchors to the phone-frame div in
        // AppLayout.jsx (which is `relative`) instead of the real browser
        // viewport — otherwise on a wide desktop window this would darken/
        // cover the whole screen instead of just the visible card.
        <div className="lg:hidden absolute inset-0 z-50 flex" data-testid="mobile-menu-drawer">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          {/* overflow-y-auto lives on <nav> only (not this outer wrapper) so
              UserCard/Sign-out stays pinned at the bottom of the drawer —
              same fix as the old desktop sidebar, so a long nav list
              (teacher/admin roles) scrolls internally instead of pushing
              sign-out below the fold.
              max-w-[85%] (not 85vw) — vw is a real-viewport unit, independent
              of the drawer's new frame-relative positioning, so on a wide
              browser it would still overflow the ~480px frame; % correctly
              resolves against this overlay's own (now frame-sized) width. */}
          <div className="relative w-80 max-w-[85%] h-full bg-sand-100 shadow-xl flex flex-col pb-[env(safe-area-inset-bottom)]">
            <div className="flex items-center justify-between px-5 pb-4 pt-[calc(1.5rem_+_env(safe-area-inset-top))]">
              <Link to="/app" onClick={() => setOpen(false)} className="flex items-center gap-2.5">
                <img src="/Hikma-Verse-Logo.png" alt="Hikmah Verses" className="h-9 w-9 rounded-xl object-cover" />
                <div>
                  <div className="font-display font-semibold text-forest-700 leading-tight">Hifz with Hikmah</div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">HikmahVerses</div>
                </div>
              </Link>
              <button onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-sand-200 transition" aria-label={t("nav.closeMenu")} data-testid="mobile-menu-close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="ornamental-line mx-7 mb-3" />
            <nav className="px-4 flex-1 min-h-0 overflow-y-auto space-y-1">
              <NavGroups user={user} onNavigate={() => setOpen(false)} />
            </nav>
            <LanguageSwitcher className="mx-4 mt-3" />
            <UserCard user={user} onLogout={async () => { setOpen(false); await logout(); }} />
          </div>
        </div>
      )}
    </>
  );
}
